import type { EChartsOption, LineSeriesOption } from 'echarts';

import {
  FONT_SIZE,
  FormatMetric,
  toBaseOption,
  toLegend,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// A radar (spider) chart on a polar grid, not ECharts' own radar: every
// metric is measured out from the centre on one shared scale, from zero,
// and each point (a metric at a category) is its own, to click. As the
// Highcharts radar: markers at each point, the first category at the top,
// each metric's line closed. The categories sit at whole turns of a value
// axis, so a line can return to its first point a full turn on (its last
// point, which is its first again).
const toPolarOption = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.PolarModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption => ({
  ...toBaseOption(theme),
  legend: toLegend({ show: model.showLegend, theme }),
  polar: {
    radius: '72%',
    center: ['50%', model.showLegend ? '46%' : '50%'],
  },
  angleAxis: {
    type: 'value',
    min: 0,
    max: Math.max(model.categories.length, 1),
    interval: 1,
    startAngle: 90,
    axisLine: { lineStyle: { color: theme.axisLine } },
    axisTick: { show: false },
    axisLabel: {
      color: theme.textPrimary,
      fontSize: FONT_SIZE,
      // A full turn is back at the first category, already labelled
      formatter: (value: number) => {
        const category =
          value < model.categories.length ? model.categories[value] : undefined;
        return category ? category.label : '';
      },
    },
    splitLine: { show: true, lineStyle: { color: theme.gridLine } },
  },
  radiusAxis: {
    type: 'value',
    min: 0,
    axisLine: { show: false },
    axisTick: { show: false },
    // Unlabelled, as the Highcharts radar all but is: its labels would sit
    // over the first category's spoke. The tooltip has the values.
    axisLabel: { show: false },
    splitLine: { lineStyle: { color: theme.gridLine } },
  },
  tooltip: {
    ...toTooltipStyle(theme),
    trigger: 'axis',
    axisPointer: { type: 'line', lineStyle: { color: theme.crosshair } },
    formatter: (params) => {
      const points = Array.isArray(params) ? params : [params];
      if (points.length === 0) {
        return '';
      }
      const index = points[0].dataIndex % model.categories.length;
      const category = model.categories[index];
      return toTooltipHtml({
        title: category ? category.title : '',
        rows: points.flatMap((point) => {
          const series = model.series.find((s) => s.id === point.seriesId);
          const value = series ? series.values[index] : null;
          return series && value !== null && value !== undefined
            ? [
                {
                  marker: typeof point.marker === 'string' ? point.marker : '',
                  name: series.name,
                  value: formatMetric({ metricId: series.metricId, value }),
                },
              ]
            : [];
        }),
      });
    },
  },
  series: model.series.map(
    (series): LineSeriesOption => ({
      type: 'line',
      coordinateSystem: 'polar',
      id: series.id,
      name: series.name,
      color: series.color,
      // [radius, angle]: each category's value at its turn, then the first
      // again a full turn on
      data: [...series.values, series.values[0] ?? null].map((value, index) => {
        const category =
          model.categories[index % Math.max(model.categories.length, 1)];
        const isDeselected =
          !!model.selectedKeys &&
          !!category &&
          !model.selectedKeys.includes(category.key);
        const point = { value: [value, index] };
        return isDeselected
          ? { ...point, itemStyle: { color: theme.deselected } }
          : point;
      }),
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        width: series.role === 'value' ? 3 : 2,
        type: series.role === 'value' ? 'solid' : 'dashed',
      },
      emphasis: {
        focus: 'series',
        lineStyle: { width: series.role === 'value' ? 5 : 3 },
      },
    }),
  ),
});

export default toPolarOption;
